import { forwardRef, type CSSProperties } from "react";
import FbkCardShell from "./FbkCardShell";
import {
  defaultFooterLeft,
  resolveTextStyle,
  type CardFormat,
  type CountdownCardContent,
  type CountdownTheme,
} from "./types";

interface CountdownCardProps {
  format: CardFormat;
  content: CountdownCardContent;
}

/**
 * Resolved palette + decoration set for one CountdownTheme.
 * Each theme is hand-tuned — no shared resolveCardTheme() here because the
 * countdown card has its own visual language (decorative SVG overlays,
 * bespoke gradients, theme-specific number styling).
 */
interface ThemePack {
  customBaseGradient: string;
  suppressBrandOverlays: boolean;
  headerOnLight: boolean;
  eyebrowColor: string;
  numberColor: string;
  /** Optional CSS background-clip gradient applied to the number itself. */
  numberGradient?: string;
  unitColor: string;
  subColor: string;
  dateChipBg: string;
  dateChipFg: string;
  ctaBg: string;
  ctaFg: string;
  /** Visual mood label rendered as a small badge under the eyebrow. */
  moodLabel: string;
  moodLabelColor: string;
}

function getThemePack(theme: CountdownTheme): ThemePack {
  switch (theme) {
    case "horizon":
      return {
        customBaseGradient:
          "radial-gradient(ellipse 90% 50% at 50% 95%, rgba(255,215,0,0.18), transparent 60%), linear-gradient(180deg, #050a1e 0%, #0e1838 45%, #1a2855 75%, #0e1838 100%)",
        suppressBrandOverlays: true,
        headerOnLight: false,
        eyebrowColor: "rgba(232, 201, 124, 0.92)",
        numberColor: "#F5E6B8",
        unitColor: "rgba(232, 201, 124, 0.85)",
        subColor: "rgba(220, 232, 255, 0.75)",
        dateChipBg: "rgba(232, 201, 124, 0.14)",
        dateChipFg: "#F5E6B8",
        ctaBg: "#F5E6B8",
        ctaFg: "#0e1838",
        moodLabel: "HORIZON",
        moodLabelColor: "rgba(220, 232, 255, 0.45)",
      };

    case "signature":
      return {
        customBaseGradient:
          "linear-gradient(135deg, #0D8C3C 0%, #0D5C2E 60%, #07321A 100%)",
        suppressBrandOverlays: false,
        headerOnLight: false,
        eyebrowColor: "#FFD700",
        numberColor: "#FFD700",
        unitColor: "rgba(255,255,255,0.92)",
        subColor: "rgba(255,255,255,0.85)",
        dateChipBg: "rgba(255,215,0,0.16)",
        dateChipFg: "#FFD700",
        ctaBg: "#FFD700",
        ctaFg: "#0A4724",
        moodLabel: "SIGNATURE",
        moodLabelColor: "rgba(255,255,255,0.45)",
      };

    case "pulse":
      return {
        customBaseGradient:
          "radial-gradient(ellipse 75% 65% at 50% 50%, #1f3a25 0%, #0d1f12 50%, #000000 100%)",
        suppressBrandOverlays: true,
        headerOnLight: false,
        eyebrowColor: "#FFD700",
        numberColor: "#FFFFFF",
        unitColor: "rgba(255,215,0,0.95)",
        subColor: "rgba(255,255,255,0.78)",
        dateChipBg: "rgba(255,215,0,0.16)",
        dateChipFg: "#FFD700",
        ctaBg: "#FFD700",
        ctaFg: "#0A4724",
        moodLabel: "PULSE",
        moodLabelColor: "rgba(255,215,0,0.55)",
      };

    case "blaze":
      return {
        customBaseGradient:
          "linear-gradient(180deg, #FFB02E 0%, #FF6B1A 35%, #E03C16 70%, #7A1A06 100%)",
        suppressBrandOverlays: true,
        headerOnLight: false,
        eyebrowColor: "#FFE3A3",
        numberColor: "#FFFFFF",
        numberGradient:
          "linear-gradient(180deg, #FFFFFF 0%, #FFE3A3 60%, #FFB02E 100%)",
        unitColor: "#FFE3A3",
        subColor: "rgba(255, 245, 220, 0.92)",
        dateChipBg: "rgba(0,0,0,0.25)",
        dateChipFg: "#FFE3A3",
        ctaBg: "#FFE3A3",
        ctaFg: "#7A1A06",
        moodLabel: "BLAZE",
        moodLabelColor: "rgba(255, 245, 220, 0.55)",
      };

    case "ignite":
      return {
        customBaseGradient:
          "linear-gradient(135deg, #FFD700 0%, #FFC93C 50%, #F2B600 100%)",
        suppressBrandOverlays: true,
        headerOnLight: true,
        eyebrowColor: "#0A4724",
        numberColor: "#07321A",
        unitColor: "#0A4724",
        subColor: "rgba(7,50,26,0.78)",
        dateChipBg: "#07321A",
        dateChipFg: "#FFD700",
        ctaBg: "#07321A",
        ctaFg: "#FFD700",
        moodLabel: "IGNITE",
        moodLabelColor: "rgba(7,50,26,0.5)",
      };
  }
}

const CountdownCard = forwardRef<HTMLDivElement, CountdownCardProps>(
  function CountdownCard({ format, content }, ref) {
    const isStory = format === "story";
    const pack = getThemePack(content.theme);
    const style = content.style ?? {};

    // Sizing — the day count is intentionally massive. On Story it eats roughly
    // half the card vertically; on Post it scales proportionally.
    const numberFontSize = isStory ? 760 : 540;
    const eyebrowFontSize = isStory ? 32 : 26;
    const unitFontSize = isStory ? 84 : 64;
    const subFontSize = isStory ? 32 : 26;
    const moodFontSize = isStory ? 16 : 14;
    const chipFontSize = isStory ? 22 : 18;

    // 1-day / 2-day cards display the value inline (small) — but for the
    // countdown card we always want the BIG number treatment.

    // Optical centering for Bebas Neue's "1" glyph. The character has a
    // wider RIGHT extension (base/foot) than left, so when the bounding box
    // is geometrically centered the visible stem sits right of the centered
    // "DAY" label below. Shift LEFT by ~3.5% of fontSize so the stem lines
    // up with the unit label. Only single-digit "1" needs it.
    // (tabular-nums dropped — Bebas Neue ships no OpenType `tnum` feature.)
    const isSingleOne = String(content.days) === "1";
    const opticalShiftPx = isSingleOne ? -Math.round(numberFontSize * 0.035) : 0;

    const numberStyle: CSSProperties = {
      fontFamily: "'Bebas Neue', 'Anton', sans-serif",
      fontSize: numberFontSize,
      fontWeight: 800,
      color: pack.numberColor,
      lineHeight: 0.85,
      letterSpacing: "-0.04em",
      textAlign: "center",
      // Apply gradient fill when the theme supplies one. Falls back to flat
      // color for export reliability (gradient text rasterizes correctly via
      // html-to-image when background-clip is set).
      ...(pack.numberGradient
        ? {
            background: pack.numberGradient,
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            backgroundClip: "text",
          }
        : {}),
      // Theme-specific glow that lifts the number off the background.
      textShadow:
        content.theme === "horizon"
          ? "0 0 60px rgba(245, 230, 184, 0.25)"
          : content.theme === "pulse"
            ? "0 0 80px rgba(255, 215, 0, 0.45), 0 4px 12px rgba(0,0,0,0.5)"
            : content.theme === "blaze"
              ? "0 6px 30px rgba(0,0,0,0.4)"
              : content.theme === "ignite"
                ? "0 4px 0 rgba(255,215,0,0.6), 0 8px 24px rgba(0,0,0,0.18)"
                : "0 6px 20px rgba(0,0,0,0.3)",
    };

    // Eyebrow / sub / cta now honour the Style tab. Theme-pack values are the
    // defaults; user overrides via style.eyebrow / style.body / style.ctaText
    // win where set.
    const eyebrowStyle: CSSProperties = {
      ...resolveTextStyle(
        style.eyebrow,
        {
          fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
          fontSizePx: eyebrowFontSize,
          color: pack.eyebrowColor,
          letterSpacing: "0.45em",
          align: "center",
          bold: true,
        },
        format,
      ),
      textTransform: "uppercase",
    };

    const unitStyle: CSSProperties = {
      fontFamily: "'Bebas Neue', 'Anton', sans-serif",
      fontSize: unitFontSize,
      fontWeight: 800,
      color: pack.unitColor,
      letterSpacing: "0.32em",
      textAlign: "center",
      lineHeight: 1,
      marginTop: isStory ? 8 : 4,
    };

    const subStyle: CSSProperties = {
      ...resolveTextStyle(
        style.body,
        {
          fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
          fontSizePx: subFontSize,
          color: pack.subColor,
          align: "center",
          bold: true,
        },
        format,
      ),
      lineHeight: 1.3,
      marginTop: isStory ? 40 : 26,
      maxWidth: isStory ? 760 : 620,
    };

    return (
      <FbkCardShell
        ref={ref}
        format={format}
        footerLeft={content.footerLeft || defaultFooterLeft("COUNTDOWN")}
        footerRight={content.hashtags || "#WC2026 · #FBK"}
        header={content.header}
        background={style.background}
        customBaseGradient={pack.customBaseGradient}
        headerOnLight={pack.headerOnLight}
        suppressBrandOverlays={pack.suppressBrandOverlays}
        hideFooter={content.footer?.hide}
      >
        <ThemeDecorations theme={content.theme} format={format} />

        {/* Main column — centered. */}
        <div
          style={{
            position: "relative",
            zIndex: 2,
            flex: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            paddingTop: isStory ? 60 : 30,
            paddingBottom: isStory ? 30 : 20,
            gap: 0,
          }}
        >
          {/* Mood label — tiny tag identifying the visual theme. */}
          <div
            style={{
              fontFamily: "ui-monospace, 'JetBrains Mono', monospace",
              fontSize: moodFontSize,
              fontWeight: 700,
              color: pack.moodLabelColor,
              letterSpacing: "0.5em",
              textTransform: "uppercase",
              marginBottom: isStory ? 18 : 14,
            }}
          >
            {pack.moodLabel}
          </div>

          <div style={eyebrowStyle}>{content.eyebrow || "WORLD CUP IN"}</div>

          {/* Hero number. minWidth slot anchors single digits at a consistent
              horizontal position across the countdown series; optical shift
              compensates for Bebas Neue's asymmetric "1" glyph (see comment
              above numberStyle). */}
          <div
            style={{
              marginTop: isStory ? 32 : 22,
              marginBottom: isStory ? 12 : 8,
              position: "relative",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <div
              style={{
                ...numberStyle,
                display: "inline-block",
                minWidth: numberFontSize * 0.55,
                transform: opticalShiftPx
                  ? `translateX(${opticalShiftPx}px)`
                  : undefined,
              }}
            >
              {content.days}
            </div>
          </div>

          <div style={unitStyle}>
            {content.unitLabel || (content.days === 1 ? "DAY" : "DAYS")}
          </div>

          {content.subText && <div style={subStyle}>{content.subText}</div>}

          {/* Date chip + optional CTA */}
          <div
            style={{
              marginTop: isStory ? 56 : 36,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 16,
            }}
          >
            {content.dateLabel && (
              <div
                style={{
                  fontFamily: "ui-monospace, 'JetBrains Mono', monospace",
                  fontSize: chipFontSize,
                  fontWeight: 800,
                  color: pack.dateChipFg,
                  background: pack.dateChipBg,
                  padding: `${isStory ? 14 : 10}px ${isStory ? 28 : 22}px`,
                  borderRadius: 9999,
                  letterSpacing: "0.25em",
                  textTransform: "uppercase",
                  border:
                    content.theme === "ignite"
                      ? "none"
                      : `1px solid ${pack.dateChipFg}33`,
                }}
              >
                {content.dateLabel}
              </div>
            )}

            {content.ctaText && (
              <div
                style={{
                  ...resolveTextStyle(
                    style.ctaText,
                    {
                      fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
                      fontSizePx: isStory ? 28 : 22,
                      color: pack.ctaFg,
                      bold: true,
                    },
                    format,
                  ),
                  background: pack.ctaBg,
                  padding: `${isStory ? 18 : 14}px ${isStory ? 40 : 30}px`,
                  borderRadius: 9999,
                  letterSpacing: "0.05em",
                  textTransform: "uppercase",
                  boxShadow: "0 14px 30px rgba(0,0,0,0.25)",
                }}
              >
                {content.ctaText}
              </div>
            )}
          </div>
        </div>
      </FbkCardShell>
    );
  },
);

export default CountdownCard;

// ─── Per-theme decorative overlays ──────────────────────────────────────────

function ThemeDecorations({
  theme,
  format,
}: {
  theme: CountdownTheme;
  format: CardFormat;
}) {
  const isStory = format === "story";
  switch (theme) {
    case "horizon":
      return <HorizonStars isStory={isStory} />;
    case "signature":
      return <SignatureGlow />;
    case "pulse":
      return <PulseRings isStory={isStory} />;
    case "blaze":
      return <BlazeRays isStory={isStory} />;
    case "ignite":
      return <IgniteMotionLines />;
  }
}

/** Star-flecked sky with a faint horizon line near the bottom. */
function HorizonStars({ isStory }: { isStory: boolean }) {
  // Deterministic pseudo-random offsets so stars are scattered but exports
  // are reproducible (no Math.random() — html-to-image needs determinism).
  const stars = Array.from({ length: 60 }).map((_, i) => {
    const x = ((i * 137.508) % 100).toFixed(2);
    const y = (((i * 78.32) + 7) % 65).toFixed(2);
    const size = 1 + ((i * 13) % 4);
    const opacity = 0.3 + ((i * 7) % 60) / 100;
    return { x, y, size, opacity, key: i };
  });
  return (
    <svg
      viewBox="0 0 100 100"
      preserveAspectRatio="none"
      style={{
        position: "absolute",
        inset: 0,
        width: "100%",
        height: "100%",
        pointerEvents: "none",
        zIndex: 1,
      }}
    >
      {stars.map((s) => (
        <circle
          key={s.key}
          cx={s.x}
          cy={s.y}
          r={s.size / 20}
          fill="#F5E6B8"
          opacity={s.opacity}
        />
      ))}
      {/* Horizon line glow */}
      <line
        x1="0"
        y1={isStory ? "75" : "80"}
        x2="100"
        y2={isStory ? "75" : "80"}
        stroke="rgba(245, 230, 184, 0.18)"
        strokeWidth="0.3"
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  );
}

/** Signature theme reuses the FBK shell overlays (handled by FbkCardShell). */
function SignatureGlow() {
  return null;
}

/** Concentric gold rings emanating from the center — pulse intensity. */
function PulseRings({ isStory }: { isStory: boolean }) {
  const center = isStory ? 50 : 50;
  const rings = [200, 280, 360, 440, 520];
  return (
    <svg
      viewBox="0 0 1000 1000"
      preserveAspectRatio="none"
      style={{
        position: "absolute",
        inset: 0,
        width: "100%",
        height: "100%",
        pointerEvents: "none",
        zIndex: 1,
      }}
    >
      {rings.map((r, i) => (
        <circle
          key={r}
          cx={center * 10}
          cy={500}
          r={r}
          fill="none"
          stroke="rgba(255, 215, 0, 0.18)"
          strokeWidth={2 - i * 0.3}
        />
      ))}
      {/* Bright halo behind the number */}
      <defs>
        <radialGradient id="pulseHalo" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="rgba(255, 215, 0, 0.35)" />
          <stop offset="100%" stopColor="rgba(255, 215, 0, 0)" />
        </radialGradient>
      </defs>
      <circle cx={center * 10} cy={500} r={300} fill="url(#pulseHalo)" />
    </svg>
  );
}

/** Sunburst rays radiating from behind the number — blaze theme. */
function BlazeRays({ isStory }: { isStory: boolean }) {
  const center = 500;
  const cy = isStory ? 500 : 500;
  const rayCount = 18;
  return (
    <svg
      viewBox="0 0 1000 1000"
      preserveAspectRatio="none"
      style={{
        position: "absolute",
        inset: 0,
        width: "100%",
        height: "100%",
        pointerEvents: "none",
        zIndex: 1,
      }}
    >
      {Array.from({ length: rayCount }).map((_, i) => {
        const angle = (i * 360) / rayCount;
        return (
          <polygon
            key={i}
            points={`${center},${cy} ${center - 30},${cy - 800} ${center + 30},${cy - 800}`}
            fill="rgba(255, 227, 163, 0.10)"
            transform={`rotate(${angle} ${center} ${cy})`}
          />
        );
      })}
      {/* Center bright halo */}
      <defs>
        <radialGradient id="blazeHalo" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="rgba(255, 244, 200, 0.45)" />
          <stop offset="100%" stopColor="rgba(255, 176, 46, 0)" />
        </radialGradient>
      </defs>
      <circle cx={center} cy={cy} r={350} fill="url(#blazeHalo)" />
    </svg>
  );
}

/** Diagonal motion lines (chevrons) suggesting speed — ignite theme. */
function IgniteMotionLines() {
  // Deeper green chevrons sliced across a pure-gold background.
  const stripes = Array.from({ length: 12 }).map((_, i) => ({
    offset: i * 180,
    opacity: 0.06 + (i % 3) * 0.04,
  }));
  return (
    <svg
      viewBox="0 0 1000 1400"
      preserveAspectRatio="none"
      style={{
        position: "absolute",
        inset: 0,
        width: "100%",
        height: "100%",
        pointerEvents: "none",
        zIndex: 1,
      }}
    >
      <g transform="rotate(-20 500 700)">
        {stripes.map((s) => (
          <rect
            key={s.offset}
            x={-200}
            y={s.offset - 200}
            width={1600}
            height={26}
            fill={`rgba(7, 50, 26, ${s.opacity})`}
          />
        ))}
      </g>
      {/* Outer punch border for sticker-vibe */}
      <rect
        x="40"
        y="40"
        width="920"
        height="1320"
        fill="none"
        stroke="rgba(7,50,26,0.18)"
        strokeWidth="6"
        rx="32"
      />
    </svg>
  );
}
